import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Paper,
} from '@mui/material';
import { VisibilityOutlined as ViewIcon } from '@mui/icons-material';
import { APP_SHAPE } from '../../constants/shape';
import { listCollectionOrders } from '../../services/order-approval.service';
import { useOrganizationStore } from '../../stores/organization.store';
import type { ApprovalOrderRow } from '../../types/order-approval.type';

const STATUS_COLOR: Record<string, 'success' | 'error' | 'info' | 'default'> = {
  completed: 'success',
  cancelled: 'error',
  in_transit: 'info',
};

/** Every route-run collection (done and cancelled) for a chosen day. */
export default function CollectionsTab() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const selectedOrganization = useOrganizationStore((state) => state.selectedOrganization);
  const [runDate, setRunDate] = useState(new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState<ApprovalOrderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const orgId = selectedOrganization?.id;
  const locale = i18n.resolvedLanguage || i18n.language;
  const number = useMemo(() => new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }), [locale]);
  const when = useMemo(
    () => new Intl.DateTimeFormat(locale, { timeStyle: 'short' }),
    [locale],
  );

  const load = useCallback(() => {
    if (!orgId) return;
    setLoading(true);
    setError(null);
    listCollectionOrders(orgId, runDate || undefined)
      .then((page) => setRows(page.orders))
      .catch(() => setError(t('collectionsMonitor.loadError')))
      .finally(() => setLoading(false));
  }, [orgId, runDate, t]);

  useEffect(() => { load(); }, [load]);

  return (
    <Paper elevation={1} sx={{ borderRadius: `${APP_SHAPE.radiusLg}px`, overflow: 'hidden' }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
          {t('collectionsMonitor.title')}
        </Typography>
        <TextField
          type="date"
          size="small"
          value={runDate}
          onChange={(event) => setRunDate(event.target.value)}
        />
      </Stack>
      {error ? <Alert severity="error" sx={{ mx: 2, mb: 2 }} action={<Button color="inherit" onClick={load}>{t('suppliers.details.retry')}</Button>}>{error}</Alert> : null}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
          {t('collectionsMonitor.empty')}
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('orderDetails.point')}</TableCell>
                <TableCell>{t('suppliers.collectionPoints.table.supplier')}</TableCell>
                <TableCell>{t('orderDetails.wasteType')}</TableCell>
                <TableCell>{t('collectionsMonitor.status')}</TableCell>
                <TableCell align="right">{t('orderDetails.weight')}</TableCell>
                <TableCell>{t('collectionsMonitor.approval')}</TableCell>
                <TableCell>{t('collectionsMonitor.time')}</TableCell>
                <TableCell align="right">{t('suppliers.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.pickup_order_id} hover>
                  <TableCell sx={{ maxWidth: 220 }}>
                    <Typography variant="body2" noWrap title={row.point_name ?? ''}>{row.point_name ?? '—'}</Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 180 }}>
                    <Typography variant="body2" noWrap title={row.supplier_name ?? ''}>{row.supplier_name ?? '—'}</Typography>
                  </TableCell>
                  <TableCell>{row.waste_type || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={STATUS_COLOR[row.status] ?? 'default'}
                      label={t(`collectionsMonitor.orderStatus.${row.status}`, row.status)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {row.verified_weight_kg != null
                      ? `${number.format(row.verified_weight_kg)} kg`
                      : row.handoff && row.estimated_weight != null
                        ? `${number.format(row.estimated_weight)} kg`
                        : '—'}
                  </TableCell>
                  <TableCell>
                    {row.approval_status ? (
                      <Tooltip
                        title={
                          row.handoff
                            ? [
                                t('collectionsMonitor.handoffTo', {
                                  facility: row.handoff.destination_facility_name ?? '',
                                  org: row.handoff.destination_org_name ?? '',
                                }),
                                row.rejection_reason ?? '',
                              ]
                                .filter(Boolean)
                                .join(' — ')
                            : (row.rejection_reason ?? '')
                        }
                      >
                        <Chip
                          size="small"
                          color={row.approval_status === 'approved' ? 'success' : row.approval_status === 'rejected' ? 'error' : 'warning'}
                          label={t(`collectionsMonitor.approvalStatus.${row.approval_status}`)}
                        />
                      </Tooltip>
                    ) : '—'}
                  </TableCell>
                  <TableCell>
                    {row.completed_at
                      ? when.format(new Date(/Z|[+-]\d\d:\d\d$/.test(row.completed_at) ? row.completed_at : `${row.completed_at}Z`))
                      : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('monitoring.orders.viewEvidence')}>
                      <IconButton size="small" onClick={() => navigate(`/orders/${row.pickup_order_id}`)}>
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}
